import React, { useMemo, useEffect, useState } from "react";
import { GripVertical, ListTree, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";
import { CONFIGURABLE_ROLES, UI_LANGS, CATALOG_BY_KEY, defaultLayout, resolveLayout, groupLabel } from "@/lib/menuCatalog";

export default function AdminMenu() {
  const { t, lang } = useLang();
  const [roles, setRoles] = useState({});
  const [role, setRole] = useState(CONFIGURABLE_ROLES[0]);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [gDrag, setGDrag] = useState(null);
  const [gOver, setGOver] = useState(null);

  useEffect(() => {
    api.get("/menu-config").then(({ data }) => setRoles(data.roles || {})).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const layout = useMemo(() => resolveLayout(role, { roles }), [role, roles]);
  const { groups, items } = layout;

  const update = (next) => setRoles((prev) => ({ ...prev, [role]: next }));

  // ---- items ----
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update({ groups, items: next });
  };
  const setVisible = (idx, v) => { const next = [...items]; next[idx] = { ...next[idx], visible: v }; update({ groups, items: next }); };
  const setItemGroup = (idx, g) => { const next = [...items]; next[idx] = { ...next[idx], group: g }; update({ groups, items: next }); };

  // ---- groups ----
  const addGroup = () => {
    const id = `g_${Date.now().toString(36)}`;
    update({ groups: [...groups, { id, labels: { nl: "", en: "", es: "" } }], items });
  };
  const removeGroup = (id) => {
    const rest = groups.filter((g) => g.id !== id);
    const firstGroup = rest[0]?.id;
    const nextItems = items.map((it) => (it.group === id ? { ...it, group: firstGroup, visible: false } : it));
    update({ groups: rest, items: nextItems });
  };
  const setGroupLabel = (id, l, value) => {
    update({ groups: groups.map((g) => (g.id === id ? { ...g, labels: { ...g.labels, [l]: value } } : g)), items });
  };
  const reorderGroups = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...groups];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update({ groups: next, items });
  };

  const reset = () => update(defaultLayout(role));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { roles: { ...roles, [role]: layout } };
      const { data } = await api.put("/menu-config", payload);
      setRoles(data.roles || {});
      toast.success(t("menu_saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Panel title={t("admin_menu")} testid="menu-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} disabled={busy} data-testid="menu-reset-button"><RotateCcw className="h-4 w-4 mr-1" />{t("menu_reset")}</Button>
          <Button onClick={save} disabled={busy} data-testid="menu-save-button">{t("save")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-5 max-w-3xl">
        <p className="text-sm text-muted-foreground flex items-start gap-2">
          <ListTree className="h-4 w-4 mt-0.5 shrink-0" />{t("admin_menu_desc")}
        </p>

        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-muted-foreground">{t("menu_role")}</span>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-56" data-testid="menu-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONFIGURABLE_ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`menu-role-${r}`}>{t(`role_${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <p className="text-[11px] text-muted-foreground">{t("menu_hint")}</p>

        {/* Groepen beheren */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("menu_groups")}</p>
            <Button size="sm" variant="outline" onClick={addGroup} data-testid="menu-add-group"><Plus className="h-4 w-4 mr-1" />{t("menu_add_group")}</Button>
          </div>
          <div className="rounded-xl border border-border divide-y divide-border" data-testid="menu-groups">
            {groups.map((g, gi) => (
              <div
                key={g.id}
                onDragOver={(e) => { e.preventDefault(); if (gOver !== gi) setGOver(gi); }}
                onDrop={(e) => { e.preventDefault(); reorderGroups(gDrag, gi); setGDrag(null); setGOver(null); }}
                onDragEnd={() => { setGDrag(null); setGOver(null); }}
                className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${gDrag === gi ? "opacity-40" : ""} ${gOver === gi && gDrag !== null && gDrag !== gi ? "bg-secondary" : ""}`}
                data-testid={`menu-group-row-${g.id}`}
              >
                <span draggable onDragStart={() => setGDrag(gi)} title={t("menu_drag")} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" data-testid={`menu-group-drag-${g.id}`}>
                  <GripVertical className="h-4 w-4" />
                </span>
                <div className="grid grid-cols-3 gap-2 flex-1">
                  {UI_LANGS.map((l) => (
                    <div key={l} className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-bold uppercase text-muted-foreground/70">{l}</span>
                      <Input value={g.labels?.[l] ?? ""} onChange={(e) => setGroupLabel(g.id, l, e.target.value)} className="pl-8 h-8 text-sm" data-testid={`menu-group-name-${g.id}-${l}`} />
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => removeGroup(g.id)} disabled={groups.length <= 1} title={t("menu_delete_group")} className="text-muted-foreground hover:text-rose-600 disabled:opacity-30" data-testid={`menu-group-delete-${g.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Items beheren */}
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("menu_items_label")}</p>
          <div className="rounded-xl border border-border divide-y divide-border" data-testid="menu-items">
            {items.map((item, idx) => {
              const cat = CATALOG_BY_KEY[item.key];
              if (!cat) return null;
              const Icon = cat.icon;
              return (
                <div
                  key={item.key}
                  onDragOver={(e) => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); }}
                  onDrop={(e) => { e.preventDefault(); reorder(dragIdx, idx); setDragIdx(null); setOverIdx(null); }}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${dragIdx === idx ? "opacity-40" : ""} ${overIdx === idx && dragIdx !== null && dragIdx !== idx ? "bg-secondary" : ""}`}
                  data-testid={`menu-row-${item.key}`}
                >
                  <span draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragIdx(idx); }} title={t("menu_drag")} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" data-testid={`menu-drag-${item.key}`}>
                    <GripVertical className="h-4 w-4" />
                  </span>
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-sm font-medium">{t(item.key)}</span>
                  <Select value={item.group} onValueChange={(g) => setItemGroup(idx, g)}>
                    <SelectTrigger className="w-44" data-testid={`menu-group-${item.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{groupLabel(g, lang) || g.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{t("menu_visible")}</span>
                    <Switch checked={item.visible !== false} onCheckedChange={(v) => setVisible(idx, v)} data-testid={`menu-visible-${item.key}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Panel>
  );
}
